// src/services/companyJourneyService.js
//
// The company activation board's one read, plus the signal that says it is
// stale.
//
// Axios through api.js, like every other authenticated /company/* read: the
// JWT comes off the request interceptor, so no caller ever passes a token.
//
// THE SIGNAL IS HERE AND NOT IN THE HOOK on purpose. Feed create, file import
// and the recruiter swipe all live in the service layer, and a service that had
// to import a React hook to announce its own write would drag React into
// modules that have no business with it. Listeners are a Set so a board that
// mounts twice under StrictMode registers and drops cleanly.
import api from '../api';
import { handleApiError } from '../utils/errorHandler';

const listeners = new Set();

/**
 * Hear about writes that could move a milestone. Returns the unsubscribe.
 *
 * A non-function is ignored rather than stored, so a caller that hands this an
 * undefined callback gets a working no-op instead of a crash on the next write.
 */
export const subscribeToJourneyChanges = (listener) => {
  if (typeof listener !== 'function') return () => {};
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/**
 * A write landed that a milestone predicate could read differently now.
 *
 * Every listener is called inside its own try/catch: the board refetching is
 * never worth failing the publish, the import or the swipe that triggered it.
 */
export const notifyJourneyChanged = () => {
  listeners.forEach((listener) => {
    try {
      listener();
    } catch {
      /* a broken listener must never fail the write that fired it */
    }
  });
};

const companyJourneyService = {
  /**
   * This account's activation journey.
   *
   * Returns { chapters, sideQuests, nextStep, premium }, derived server-side on
   * every read. Throws an Error carrying `.status` and `.response`, which is how
   * a caller tells the ADMIN 400 apart from a real outage.
   */
  getJourney: async () => {
    try {
      const response = await api.get('/company/journey');
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'Failed to load your journey');
    }
  },
};

export default companyJourneyService;
